/**
 * WordPress dependencies
 */
import {
	__EXPERIMENTAL_STYLE_PROPERTY as STYLE_PROPERTY,
	__EXPERIMENTAL_ELEMENTS as ELEMENTS,
	getBlockSupport,
	getBlockTypes,
	store as blocksStore,
} from '@wordpress/blocks';
import type { BlockType } from '@wordpress/blocks';
import { getCSSRules, getCSSValueFromRawStyle } from '@wordpress/style-engine';
import { select } from '@wordpress/data';

/**
 * Internal dependencies
 */
import {
	PRESET_METADATA,
	ROOT_BLOCK_SELECTOR,
	ROOT_CSS_PROPERTIES_SELECTOR,
	scopeSelector,
	scopeFeatureSelectors,
	appendToSelector,
	getBlockStyleVariationSelector,
	getResolvedValue,
} from '../utils/common';
import { getBlockSelector } from './selectors';
import { getTypographyFontSizeValue } from '../utils/typography';
import { getDuotoneFilter } from '../utils/duotone';
import { kebabCase } from '../utils/string';
import { getGapCSSValue } from '../utils/gap';
import { setBackgroundStyleDefaults } from '../utils/background';
import { LAYOUT_DEFINITIONS } from '../utils/layout';
import { getValueFromObjectPath, setImmutably } from '../utils/object';
import { getSetting } from '../settings/get-setting';
import type { GlobalStylesConfig, GlobalStylesStyles } from '../types';

// =============================================================================
// LOCAL TYPE DEFINITIONS
// =============================================================================

/**
 * Preset metadata for CSS variable generation
 */
interface PresetMetadata {
	path: string[];
	valueKey?: string;
	valueFunc?: ( preset: any, settings: any ) => string | number | null;
	cssVarInfix: string;
	classes?: Array< {
		classSuffix: string;
		propertyName: string;
	} >;
}

/**
 * Preset collection by origin
 */
interface PresetsByOrigin {
	[ origin: string ]: any[];
}

/**
 * CSS class configuration
 */
interface CSSClassConfig {
	classSuffix: string;
	propertyName: string;
}

/**
 * Style property configuration from WordPress
 */
interface StylePropertyConfig {
	value: string[];
	properties?: Record< string, string >;
	useEngine?: boolean;
	rootOnly?: boolean;
}

/**
 * Layout definition structure
 */
interface LayoutDefinition {
	className: string;
	name: string;
	displayMode?: string;
	spacingStyles?: Array< {
		selector?: string;
		rules?: Record< string, any >;
	} >;
	baseStyles?: Array< {
		selector?: string;
		rules?: Record< string, any >;
	} >;
}

/**
 * CSS rule from style engine
 */
interface CSSRule {
	key: string;
	value: any;
}

/**
 * Block variation in theme.json (different from BlockStyleVariation)
 */
interface BlockVariation {
	css?: string;
	elements?: Record< string, any >;
	blocks?: Record< string, any >;
	[ key: string ]: any; // For additional style properties
}

/**
 * Block node in theme.json
 */
interface BlockNode {
	variations?: Record< string, BlockVariation >;
	elements?: Record< string, any >;
	[ key: string ]: any; // For additional style properties
}

export type BlockSelectors = Record<
	string,
	{
		duotoneSelector?: string;
		selector: string;
		fallbackGapValue?: string;
		hasLayoutSupport?: boolean;
		featureSelectors?:
			| string
			| Record< string, string | Record< string, string > >;
		name?: string;
		styleVariationSelectors?: Record< string, string >;
	}
>;

/**
 * Style node metadata used to render one selector's style rules.
 *
 * - `styles`: theme.json style object for this node.
 * - `selector`: CSS selector used for the node's base declarations.
 * - `selectorSuffix`: optional suffix used to append additional selectors,
 *   such as pseudo selectors, to base and feature selectors.
 * - `mediaQuery`: optional media query wrapping this node's rules.
 * - `skipSelectorWrapper`: omits the `:root :where()` specificity wrapper.
 * - `duotoneSelector`: alternate selector for duotone filter declarations.
 * - `featureSelectors`: feature-level selectors for block supports.
 * - `fallbackGapValue`: fallback block gap value used by layout rules.
 * - `hasLayoutSupport`: whether layout styles can be generated for the node.
 * - `isStyleVariation`: whether this node is a block style variation.
 * - `layoutSelector`: optional selector override for layout styles.
 * - `layoutHasBlockGapSupport`: optional block gap support override for layout styles.
 * - `name`: block name used by block-specific declaration adjustments.
 * - `elementName`: element name used to resolve valid pseudo selectors.
 */
interface StylesNode {
	styles: any;
	selector: string;
	selectorSuffix?: string;
	mediaQuery?: string;
	skipSelectorWrapper?: boolean;
	duotoneSelector?: string;
	featureSelectors?:
		| string
		| Record< string, string | Record< string, string > >;
	fallbackGapValue?: string;
	hasLayoutSupport?: boolean;
	isStyleVariation?: boolean;
	layoutSelector?: string;
	layoutHasBlockGapSupport?: boolean;
	name?: string;
	elementName?: string;
}

type ElementName = keyof typeof ELEMENTS;

// Elements that rely on class names in their selectors.
const ELEMENT_CLASS_NAMES = {
	button: 'wp-element-button',
	caption: 'wp-element-caption',
};

// List of block support features that can have their related styles
// generated under their own feature level selector rather than the block's.
const BLOCK_SUPPORT_FEATURE_LEVEL_SELECTORS = {
	__experimentalBorder: 'border',
	color: 'color',
	dimensions: 'dimensions',
	spacing: 'spacing',
	typography: 'typography',
};

// The valid pseudo-selectors that can be used for blocks.
// Keep in sync with WP_Theme_JSON_Gutenberg::VALID_BLOCK_PSEUDO_SELECTORS.
const VALID_BLOCK_PSEUDO_SELECTORS: Record< string, string[] > = {
	'core/button': [ ':hover', ':focus', ':focus-visible', ':active' ],
	'core/navigation-link': [ ':hover', ':focus', ':focus-visible', ':active' ],
};

// The valid pseudo-selectors that can be used for elements.
// Keep in sync with WP_Theme_JSON_Gutenberg::VALID_ELEMENT_PSEUDO_SELECTORS.
const VALID_ELEMENT_PSEUDO_SELECTORS: Record< string, string[] > = {
	link: [
		':link',
		':any-link',
		':visited',
		':hover',
		':focus',
		':focus-visible',
		':active',
	],
	button: [
		':link',
		':any-link',
		':visited',
		':hover',
		':focus',
		':focus-visible',
		':active',
	],
};

/**
 * Responsive breakpoint state keys and their corresponding CSS media queries.
 * Keep in sync with WP_Theme_JSON_Gutenberg::RESPONSIVE_BREAKPOINTS.
 */
const RESPONSIVE_BREAKPOINTS: Record< string, string > = {
	mobile: '@media (width <= 480px)',
	tablet: '@media (480px < width <= 782px)',
};

/**
 * Transform given preset tree into a set of preset class declarations.
 *
 * @param blockSelector Block selector string
 * @param blockPresets  Block presets object
 * @return CSS declarations for the preset classes
 */
function getPresetsClasses(
	blockSelector: string = '*',
	blockPresets: Record< string, any > = {}
): string {
	return PRESET_METADATA.reduce(
		(
			declarations: string,
			{ path, cssVarInfix, classes }: PresetMetadata
		) => {
			if ( ! classes ) {
				return declarations;
			}

			const presetByOrigin = getValueFromObjectPath(
				blockPresets,
				path,
				[]
			) as PresetsByOrigin;
			[ 'default', 'theme', 'custom' ].forEach( ( origin ) => {
				if ( presetByOrigin[ origin ] ) {
					presetByOrigin[ origin ].forEach(
						( { slug }: { slug: string } ) => {
							classes!.forEach(
								( {
									classSuffix,
									propertyName,
								}: CSSClassConfig ) => {
									const classSelectorToUse = `.has-${ kebabCase(
										slug
									) }-${ classSuffix }`;
									const selectorToUse = blockSelector
										.split( ',' ) // Selector can be "h1, h2, h3"
										.map(
											( selector ) =>
												`${ selector }${ classSelectorToUse }`
										)
										.join( ',' );
									const value = `var(--wp--preset--${ cssVarInfix }--${ kebabCase(
										slug
									) })`;
									declarations += `${ selectorToUse }{${ propertyName }: ${ value } !important;}`;
								}
							);
						}
					);
				}
			} );
			return declarations;
		},
		''
	);
}

function getPresetsSvgFilters(
	blockPresets: Record< string, any > = {}
): string[] {
	return PRESET_METADATA.filter(
		// Duotone are the only type of filters for now.
		( metadata: PresetMetadata ) => metadata.path.at( -1 ) === 'duotone'
	).flatMap( ( metadata: PresetMetadata ) => {
		const presetByOrigin = getValueFromObjectPath(
			blockPresets,
			metadata.path,
			{}
		) as PresetsByOrigin;
		return [ 'default', 'theme' ]
			.filter( ( origin ) => presetByOrigin[ origin ] )
			.flatMap( ( origin ) =>
				presetByOrigin[ origin ].map( ( preset: any ) =>
					getDuotoneFilter(
						`wp-duotone-${ preset.slug }`,
						preset.colors
					)
				)
			)
			.join( '' );
	} );
}

function flattenTree(
	input: any = {},
	prefix: string,
	token: string
): string[] {
	let result: string[] = [];
	Object.keys( input ).forEach( ( key ) => {
		const newKey = prefix + kebabCase( key.replace( '/', '-' ) );
		const newLeaf = input[ key ];

		if ( newLeaf instanceof Object ) {
			const newPrefix = newKey + token;
			result = [ ...result, ...flattenTree( newLeaf, newPrefix, token ) ];
		} else {
			result.push( `${ newKey }: ${ newLeaf }` );
		}
	} );
	return result;
}

/**
 * Gets variation selector string from feature selector.
 *
 * @param featureSelector        The feature selector
 * @param styleVariationSelector The style variation selector
 * @return Combined selector string
 */
function concatFeatureVariationSelectorString(
	featureSelector: string,
	styleVariationSelector: string
): string {
	const featureSelectors = featureSelector.split( ',' );
	const combinedSelectors: string[] = [];
	featureSelectors.forEach( ( selector ) => {
		combinedSelectors.push(
			`${ styleVariationSelector.trim() }${ selector.trim() }`
		);
	} );
	return combinedSelectors.join( ', ' );
}

/**
 * Updates the text indent selector for paragraph blocks based on the textIndent setting.
 *
 * The textIndent setting can be 'subsequent' (default), 'all', or false.
 * When set to 'all', the selector should be '.wp-block-paragraph' instead of
 * '.wp-block-paragraph + .wp-block-paragraph' to apply indent to all paragraphs.
 *
 * @param featureDeclarations The feature declarations keyed by selector
 * @param settings            Theme.json settings
 * @param blockName           The name of the block being processed
 * @return Updated feature declarations
 */
const updateParagraphTextIndentSelector = (
	featureDeclarations: Record< string, string[] >,
	settings: Record< string, any > | undefined,
	blockName: string | undefined
): Record< string, string[] > => {
	if ( blockName !== 'core/paragraph' ) {
		return featureDeclarations;
	}

	// Check block-level settings first, then fall back to global settings.
	const blockSettings = settings?.blocks?.[ 'core/paragraph' ];
	const textIndentSetting =
		blockSettings?.typography?.textIndent ??
		settings?.typography?.textIndent ??
		'subsequent';

	if ( textIndentSetting !== 'all' ) {
		return featureDeclarations;
	}

	// Look for the text indent selector and replace it.
	const oldSelector = '.wp-block-paragraph + .wp-block-paragraph';
	const newSelector = '.wp-block-paragraph';

	if ( oldSelector in featureDeclarations ) {
		const declarations = featureDeclarations[ oldSelector ];
		const updated = { ...featureDeclarations };
		delete updated[ oldSelector ];
		updated[ newSelector ] = declarations;
		return updated;
	}

	return featureDeclarations;
};

/**
 * Updates button width declarations to use a calc() formula for percentage values.
 *
 * When a percentage width is set on the Button block via Global Styles, the
 * resulting CSS needs to account for block gap spacing so that buttons tile
 * correctly on a row (e.g. 4 buttons at 25% width all fit on one row).
 *
 * This mirrors the dynamic calc() formula applied at the block instance level
 * in the button block's stylesheet (style.scss).
 *
 * @param featureDeclarations Feature declarations keyed by selector.
 * @param settings            The theme.json settings.
 * @return Updated feature declarations.
 */
const updateButtonWidthDeclarations = (
	featureDeclarations: Record< string, string[] >,
	settings: Record< string, any > | undefined
): Record< string, string[] > => {
	const buttonSelector = '.wp-block-button';
	if ( ! ( buttonSelector in featureDeclarations ) ) {
		return featureDeclarations;
	}

	const updated = { ...featureDeclarations };
	updated[ buttonSelector ] = updated[ buttonSelector ].map(
		( declaration ) => {
			// Match "width: <value>" declarations.
			const match = declaration.match( /^width:\s*(.+)$/ );
			if ( ! match ) {
				return declaration;
			}

			const value = match[ 1 ];
			let percentage: number | null = null;

			// Case 1: Direct percentage value e.g. "25%".
			if ( value.endsWith( '%' ) ) {
				percentage = parseFloat( value );
			}

			// Case 2: Preset CSS var e.g. "var(--wp--preset--dimension--50)".
			const presetPrefix = 'var(--wp--preset--dimension--';
			if (
				percentage === null &&
				value.startsWith( presetPrefix ) &&
				value.endsWith( ')' )
			) {
				const slug = value.slice( presetPrefix.length, -1 );

				/*
				 * Look up the preset size across all origins.
				 * Check block-level settings first (core/button), then top-level settings.
				 * Spread block-level entries first so they take precedence.
				 */
				const dimensionSizes = {
					...( settings?.dimensions?.dimensionSizes ?? {} ),
					...( settings?.blocks?.[ 'core/button' ]?.dimensions
						?.dimensionSizes ?? {} ),
				};
				for ( const origin of Object.values( dimensionSizes ) ) {
					if ( ! Array.isArray( origin ) ) {
						continue;
					}
					for ( const preset of origin ) {
						if (
							preset.slug === slug &&
							typeof preset.size === 'string' &&
							preset.size.endsWith( '%' )
						) {
							percentage = parseFloat( preset.size );
							break;
						}
					}
					if ( percentage !== null ) {
						break;
					}
				}
			}

			if ( percentage === null || isNaN( percentage ) ) {
				return declaration;
			}

			/*
			 * Apply the same calc() formula as the block instance level (style.scss).
			 * The numeric percentage value is used as a unitless number:
			 * - Multiplied by 1% to get the percentage width.
			 * - Divided by 100 to calculate the gap adjustment proportion.
			 */
			return `width: calc(${ percentage } * 1% - (var(--wp--style--block-gap, 0.5em) * (1 - ${ percentage } / 100)))`;
		}
	);

	return updated;
};

/**
 * Generate style declarations for a block's custom feature and subfeature
 * selectors.
 *
 * NOTE: The passed `styles` object will be mutated by this function.
 *
 * @param selectors Custom selectors object for a block
 * @param styles    A block's styles object
 * @return Style declarations
 */
const getFeatureDeclarations = (
	selectors: Record< string, any >,
	styles: Record< string, any >
): Record< string, string[] > => {
	const declarations: Record< string, string[] > = {};

	Object.entries( selectors ).forEach( ( [ feature, selector ] ) => {
		// We're only processing features/subfeatures that have styles.
		if ( feature === 'root' || ! styles?.[ feature ] ) {
			return;
		}

		const isShorthand = typeof selector === 'string';

		// If we have a selector object instead of shorthand process it.
		if (
			! isShorthand &&
			typeof selector === 'object' &&
			selector !== null
		) {
			Object.entries( selector as Record< string, string > ).forEach(
				( [ subfeature, subfeatureSelector ] ) => {
					// Don't process root feature selector yet or any
					// subfeature that doesn't have a style.
					if (
						subfeature === 'root' ||
						! styles?.[ feature ][ subfeature ]
					) {
						return;
					}

					// Create a temporary styles object and build
					// declarations for subfeature.
					const subfeatureStyles = {
						[ feature ]: {
							[ subfeature ]: styles[ feature ][ subfeature ],
						},
					};
					const newDeclarations =
						getStylesDeclarations( subfeatureStyles );

					// Merge new declarations in with any others that
					// share the same selector.
					declarations[ subfeatureSelector ] = [
						...( declarations[ subfeatureSelector ] || [] ),
						...newDeclarations,
					];

					// Remove the subfeature's style now it will be
					// included under its own selector not the block's.
					delete styles[ feature ][ subfeature ];
				}
			);
		}

		// Now subfeatures have been processed and removed, we can
		// process root, or shorthand, feature selectors.
		if (
			isShorthand ||
			( typeof selector === 'object' &&
				selector !== null &&
				'root' in selector )
		) {
			const featureSelector = isShorthand
				? ( selector as string )
				: ( selector as any ).root;

			// Create temporary style object and build declarations for feature.
			const featureStyles = { [ feature ]: styles[ feature ] };
			const newDeclarations = getStylesDeclarations( featureStyles );

			// Merge new declarations with any others that share the selector.
			declarations[ featureSelector ] = [
				...( declarations[ featureSelector ] || [] ),
				...newDeclarations,
			];

			// Remove the feature from the block's styles now as it will be
			// included under its own selector not the block's.
			delete styles[ feature ];
		}
	} );

	return declarations;
};

/**
 * Transform given style tree into a set of style declarations.
 *
 * @param blockStyles         Block styles
 * @param selector            The selector these declarations should attach to
 * @param useRootPaddingAlign Whether to use CSS custom properties in root selector
 * @param tree                A theme.json tree containing layout definitions
 * @param disableRootPadding  Whether to force disable the root padding styles
 * @return An array of style declarations
 */
export function getStylesDeclarations(
	blockStyles: any = {},
	selector: string = '',
	useRootPaddingAlign?: boolean,
	tree: any = {},
	disableRootPadding: boolean = false
): string[] {
	const isRoot = ROOT_BLOCK_SELECTOR === selector;
	const output = Object.entries(
		STYLE_PROPERTY as Record< string, StylePropertyConfig >
	).reduce(
		(
			declarations: string[],
			[ key, { value, properties, useEngine, rootOnly } ]: [
				string,
				StylePropertyConfig,
			]
		) => {
			if ( rootOnly && ! isRoot ) {
				return declarations;
			}
			const pathToValue = value;
			if ( pathToValue[ 0 ] === 'elements' || useEngine ) {
				return declarations;
			}

			const styleValue = getValueFromObjectPath(
				blockStyles,
				pathToValue
			);

			// Root-level padding styles don't currently support strings with CSS shorthand values.
			// This may change: https://github.com/WordPress/gutenberg/issues/40132.
			if (
				key === '--wp--style--root--padding' &&
				( typeof styleValue === 'string' || ! useRootPaddingAlign )
			) {
				return declarations;
			}

			if ( properties && typeof styleValue !== 'string' ) {
				Object.entries( properties ).forEach( ( entry ) => {
					const [ name, prop ] = entry;

					if (
						! getValueFromObjectPath( styleValue, [ prop ], false )
					) {
						// Do not create a declaration
						// for sub-properties that don't have any value.
						return;
					}

					const cssProperty = name.startsWith( '--' )
						? name
						: kebabCase( name );
					declarations.push(
						`${ cssProperty }: ${ getCSSValueFromRawStyle(
							getValueFromObjectPath( styleValue, [ prop ] )
						) }`
					);
				} );
			} else if (
				getValueFromObjectPath( blockStyles, pathToValue, false )
			) {
				const cssProperty = key.startsWith( '--' )
					? key
					: kebabCase( key );
				declarations.push(
					`${ cssProperty }: ${ getCSSValueFromRawStyle(
						getValueFromObjectPath( blockStyles, pathToValue )
					) }`
				);
			}

			return declarations;
		},
		[] as string[]
	);

	/*
	 * Preprocess background image values.
	 *
	 * Note: As we absorb more and more styles into the engine, we could simplify this function.
	 * A refactor is for the style engine to handle ref resolution (and possibly defaults)
	 * via a public util used internally and externally. Theme.json tree and defaults could be passed
	 * as options.
	 */
	if ( !! blockStyles.background ) {
		/*
		 * Resolve dynamic values before they are compiled by the style engine,
		 * which doesn't (yet) resolve dynamic values.
		 */
		if ( blockStyles.background?.backgroundImage ) {
			blockStyles.background.backgroundImage = getResolvedValue(
				blockStyles.background.backgroundImage,
				tree
			);
		}

		/*
		 * Set default values for block background styles.
		 * Top-level styles are an exception as they are applied to the body.
		 */
		if ( ! isRoot && !! blockStyles.background?.backgroundImage?.id ) {
			blockStyles = {
				...blockStyles,
				background: {
					...blockStyles.background,
					...setBackgroundStyleDefaults( blockStyles.background ),
				},
			};
		}
	}

	const extraRules = getCSSRules( blockStyles );
	extraRules.forEach( ( rule: CSSRule ) => {
		// Don't output padding properties if padding variables are set or if we're not editing a full template.
		if (
			isRoot &&
			( useRootPaddingAlign || disableRootPadding ) &&
			rule.key.startsWith( 'padding' )
		) {
			return;
		}
		const cssProperty = rule.key.startsWith( '--' )
			? rule.key
			: kebabCase( rule.key );

		let ruleValue = getResolvedValue( rule.value, tree );

		// Calculate fluid typography rules where available.
		if ( cssProperty === 'font-size' ) {
			/*
			 * getTypographyFontSizeValue() will check
			 * if fluid typography has been activated and also
			 * whether the incoming value can be converted to a fluid value.
			 * Values that already have a "clamp()" function will not pass the test,
			 * and therefore the original $value will be returned.
			 */
			ruleValue = getTypographyFontSizeValue(
				{ name: '', slug: '', size: ruleValue as string },
				tree?.settings
			);
		}

		// For aspect ratio to work, other dimensions rules (and Cover block defaults) must be unset.
		// This ensures that a fixed height does not override the aspect ratio.
		if ( cssProperty === 'aspect-ratio' ) {
			output.push( 'min-height: unset' );
		}

		output.push( `${ cssProperty }: ${ ruleValue }` );
	} );

	return output;
}

/**
 * Get generated CSS for layout styles by looking up layout definitions provided
 * in theme.json, and outputting common layout styles, and specific blockGap values.
 *
 * @param props                       Layout styles configuration
 * @param props.layoutDefinitions     Layout definitions from theme.json
 * @param props.style                 Style object for the block
 * @param props.selector              Selector to apply the styles to
 * @param props.hasBlockGapSupport    Whether the block supports block gap styles
 * @param props.hasFallbackGapSupport Whether the block supports fallback gap styles
 * @param props.fallbackGapValue      Fallback gap value to use if block gap support is
 *
 * @return Generated CSS rules for the layout styles
 */
export function getLayoutStyles( {
	layoutDefinitions = LAYOUT_DEFINITIONS,
	style,
	selector,
	hasBlockGapSupport,
	hasFallbackGapSupport,
	fallbackGapValue,
}: {
	layoutDefinitions?: Record< string, LayoutDefinition >;
	style?: GlobalStylesStyles;
	selector?: string;
	hasBlockGapSupport?: boolean;
	hasFallbackGapSupport?: boolean;
	fallbackGapValue?: string;
} ): string {
	let ruleset = '';
	let gapValue = hasBlockGapSupport
		? getGapCSSValue( style?.spacing?.blockGap )
		: '';

	// Ensure a fallback gap value for the root layout definitions,
	// and use a fallback value if one is provided for the current block.
	if ( hasFallbackGapSupport ) {
		if ( selector === ROOT_BLOCK_SELECTOR ) {
			gapValue = ! gapValue ? '0.5em' : gapValue;
		} else if ( ! hasBlockGapSupport && fallbackGapValue ) {
			gapValue = fallbackGapValue;
		}
	}

	if ( gapValue && layoutDefinitions ) {
		Object.values( layoutDefinitions ).forEach(
			( { className, name, spacingStyles } ) => {
				// Allow outputting fallback gap styles for flex layout type when block gap support isn't available.
				if (
					! hasBlockGapSupport &&
					'flex' !== name &&
					'grid' !== name
				) {
					return;
				}

				if ( spacingStyles?.length ) {
					spacingStyles.forEach( ( spacingStyle: any ) => {
						const declarations: string[] = [];

						if ( spacingStyle.rules ) {
							Object.entries( spacingStyle.rules ).forEach(
								( [ cssProperty, cssValue ] ) => {
									declarations.push(
										`${ cssProperty }: ${
											cssValue ? cssValue : gapValue
										}`
									);
								}
							);
						}

						if ( declarations.length ) {
							let combinedSelector = '';

							if ( ! hasBlockGapSupport ) {
								// For fallback gap styles, use lower specificity, to ensure styles do not unintentionally override theme styles.
								combinedSelector =
									selector === ROOT_BLOCK_SELECTOR
										? `:where(.${ className }${
												spacingStyle?.selector || ''
										  })`
										: `:where(${ selector }.${ className }${
												spacingStyle?.selector || ''
										  })`;
							} else {
								combinedSelector =
									selector === ROOT_BLOCK_SELECTOR
										? `:root :where(.${ className })${
												spacingStyle?.selector || ''
										  }`
										: `:root :where(${ selector }-${ className })${
												spacingStyle?.selector || ''
										  }`;
							}
							ruleset += `${ combinedSelector } { ${ declarations.join(
								'; '
							) }; }`;
						}
					} );
				}
			}
		);
		// For backwards compatibility, ensure the legacy block gap CSS variable is still available.
		if ( selector === ROOT_BLOCK_SELECTOR && hasBlockGapSupport ) {
			ruleset += `${ ROOT_CSS_PROPERTIES_SELECTOR } { --wp--style--block-gap: ${ gapValue }; }`;
		}
	}

	// Output base styles
	if ( selector === ROOT_BLOCK_SELECTOR && layoutDefinitions ) {
		const validDisplayModes = [ 'block', 'flex', 'grid' ];
		Object.values( layoutDefinitions ).forEach(
			( { className, displayMode, baseStyles }: LayoutDefinition ) => {
				if (
					displayMode &&
					validDisplayModes.includes( displayMode )
				) {
					ruleset += `${ selector } .${ className } { display:${ displayMode }; }`;
				}

				if ( baseStyles?.length ) {
					baseStyles.forEach( ( baseStyle: any ) => {
						const declarations: string[] = [];

						if ( baseStyle.rules ) {
							Object.entries( baseStyle.rules ).forEach(
								( [ cssProperty, cssValue ] ) => {
									declarations.push(
										`${ cssProperty }: ${ cssValue }`
									);
								}
							);
						}

						if ( declarations.length ) {
							const combinedSelector = `.${ className }${
								baseStyle?.selector || ''
							}`;
							ruleset += `${ combinedSelector } { ${ declarations.join(
								'; '
							) }; }`;
						}
					} );
				}
			}
		);
	}

	return ruleset;
}

const STYLE_KEYS = [
	'border',
	'color',
	'dimensions',
	'spacing',
	'typography',
	'filter',
	'outline',
	'shadow',
	'background',
];

function pickStyleKeys( treeToPickFrom: any ): any {
	return pickStyleAndPseudoKeys( treeToPickFrom );
}

function pickStyleAndPseudoKeys(
	treeToPickFrom: any,
	blockName?: string
): any {
	if ( ! treeToPickFrom ) {
		return {};
	}
	const entries = Object.entries( treeToPickFrom );
	const allowedPseudoSelectors = blockName
		? VALID_BLOCK_PSEUDO_SELECTORS[ blockName ] ?? []
		: [];

	const pickedEntries = entries.filter(
		( [ key ] ) =>
			STYLE_KEYS.includes( key ) ||
			allowedPseudoSelectors.includes( key ) ||
			RESPONSIVE_BREAKPOINTS[ key ]
	);
	// clone the style objects so that `getFeatureDeclarations` can remove consumed keys from it
	const clonedEntries = pickedEntries.map( ( [ key, style ] ) => [
		key,
		JSON.parse( JSON.stringify( style ) ),
	] );
	return Object.fromEntries( clonedEntries );
}

/**
 * Creates style nodes for configured block and element pseudo selectors.
 *
 * Only pseudo selectors listed in the matching block or element allow-list are
 * considered. This mirrors the PHP renderer and avoids treating arbitrary
 * colon-prefixed keys as pseudo selectors.
 *
 * @param node Style node that may contain configured pseudo styles.
 * @return Style nodes for the configured pseudo states.
 */
function getPseudoStyleNodes( node: StylesNode ): StylesNode[] {
	const {
		styles,
		selector,
		featureSelectors,
		name,
		elementName,
		mediaQuery,
	} = node;
	const pseudoSelectors = name
		? VALID_BLOCK_PSEUDO_SELECTORS[ name ] ?? []
		: VALID_ELEMENT_PSEUDO_SELECTORS[ elementName ?? '' ] ?? [];

	if ( ! pseudoSelectors.length ) {
		return [];
	}

	return pseudoSelectors.flatMap( ( pseudoSelector ) => {
		const pseudoStyles = styles?.[ pseudoSelector ];
		if ( ! pseudoStyles || typeof pseudoStyles !== 'object' ) {
			return [];
		}

		return [
			{
				styles: JSON.parse( JSON.stringify( pseudoStyles ) ),
				selector,
				selectorSuffix: pseudoSelector,
				mediaQuery,
				featureSelectors:
					featureSelectors && typeof featureSelectors !== 'string'
						? featureSelectors
						: undefined,
				name,
				elementName,
			},
		];
	} );
}

/**
 * Creates style nodes for configured responsive breakpoint states.
 *
 * Breakpoint nodes render feature-level, base, and pseudo declarations through
 * the normal node renderer.
 *
 * @param node Style node that may contain configured responsive state styles.
 * @return Responsive style nodes in configured breakpoint order.
 */
function getResponsiveStyleNodes( node: StylesNode ): StylesNode[] {
	const {
		styles,
		selector,
		featureSelectors,
		name,
		elementName,
		isStyleVariation,
	} = node;

	if ( ! name && ! elementName ) {
		return [];
	}

	return Object.entries( RESPONSIVE_BREAKPOINTS ).flatMap(
		( [ breakpointKey, mediaQuery ] ) => {
			const breakpointStyles = styles?.[ breakpointKey ];
			if ( ! breakpointStyles || typeof breakpointStyles !== 'object' ) {
				return [];
			}

			return [
				{
					styles: JSON.parse( JSON.stringify( breakpointStyles ) ),
					selector,
					mediaQuery,
					featureSelectors:
						featureSelectors && typeof featureSelectors !== 'string'
							? featureSelectors
							: undefined,
					name,
					elementName,
					isStyleVariation,
				},
			];
		}
	);
}

/**
 * Scopes feature selectors to a style variation selector.
 *
 * Variation feature selectors are compound selectors rather than suffixes. For
 * example, `.wp-image-spacing` becomes `.is-style-foo.wp-image.wp-image-spacing`.
 *
 * @param featureSelectors       Feature-level selectors from a style node.
 * @param styleVariationSelector Selector for the style variation.
 * @return Feature-level selectors scoped to the style variation.
 */
function getVariationFeatureSelectors(
	featureSelectors: StylesNode[ 'featureSelectors' ],
	styleVariationSelector: string
): StylesNode[ 'featureSelectors' ] {
	if ( ! featureSelectors || typeof featureSelectors === 'string' ) {
		return undefined;
	}

	return Object.fromEntries(
		Object.entries( featureSelectors ).map( ( [ feature, selector ] ) => {
			if ( typeof selector === 'string' ) {
				return [
					feature,
					concatFeatureVariationSelectorString(
						selector,
						styleVariationSelector
					),
				];
			}

			return [
				feature,
				Object.fromEntries(
					Object.entries( selector ).map(
						( [ subfeature, subfeatureSelector ] ) => [
							subfeature,
							concatFeatureVariationSelectorString(
								subfeatureSelector,
								styleVariationSelector
							),
						]
					)
				),
			];
		} )
	);
}

export const getNodesWithStyles = (
	tree: GlobalStylesConfig,
	blockSelectors: string | BlockSelectors
): any[] => {
	const nodes: StylesNode[] = [];

	if ( ! tree?.styles ) {
		return nodes;
	}

	// Top-level.
	const styles = pickStyleKeys( tree.styles );
	if ( styles ) {
		nodes.push( {
			styles,
			selector: ROOT_BLOCK_SELECTOR,
			// Root selector (body) styles should not be wrapped in `:root where()` to keep
			// specificity at (0,0,1) and maintain backwards compatibility.
			skipSelectorWrapper: true,
		} );
	}

	Object.entries( ELEMENTS ).forEach( ( [ name, selector ] ) => {
		if ( tree.styles?.elements?.[ name ] ) {
			nodes.push( {
				styles: tree.styles?.elements?.[ name ] ?? {},
				selector: selector as string,
				elementName: name,
				// Top level elements that don't use a class name should not receive the
				// `:root :where()` wrapper to maintain backwards compatibility.
				skipSelectorWrapper: ! (
					ELEMENT_CLASS_NAMES as Record< string, string >
				 )[ name ],
			} );
		}
	} );

	// Iterate over blocks: they can have styles & elements.
	Object.entries( tree.styles?.blocks ?? {} ).forEach(
		( [ blockName, node ] ) => {
			const blockStyles = pickStyleAndPseudoKeys( node, blockName );
			const typedNode = node as BlockNode;

			// Store variation child nodes so they can be inserted after the block's own elements.
			const variationNodesToAdd: typeof nodes = [];
			const variationStyleNodesToAdd: typeof nodes = [];

			if ( typedNode?.variations ) {
				Object.entries( typedNode.variations ).forEach(
					( [ variationName, variation ] ) => {
						const typedVariation = variation as BlockVariation;
						const variationStyles = pickStyleAndPseudoKeys(
							typedVariation,
							blockName
						);
						if ( typedVariation?.css ) {
							variationStyles.css = typedVariation.css;
						}
						const variationSelector =
							typeof blockSelectors !== 'string'
								? blockSelectors[ blockName ]
										?.styleVariationSelectors?.[
										variationName
								  ]
								: undefined;
						if (
							variationSelector &&
							typeof blockSelectors !== 'string'
						) {
							const blockSelector = blockSelectors[ blockName ];
							variationStyleNodesToAdd.push( {
								styles: variationStyles,
								selector: variationSelector,
								featureSelectors: getVariationFeatureSelectors(
									blockSelector?.featureSelectors,
									variationSelector
								),
								fallbackGapValue:
									blockSelector?.fallbackGapValue,
								hasLayoutSupport:
									blockSelector?.hasLayoutSupport,
								isStyleVariation: true,
								layoutSelector:
									variationSelector + blockSelector.selector,
								layoutHasBlockGapSupport: true,
								name: blockName,
							} );
						}

						// Process the variation's inner element styles.
						// This comes before the inner block styles so the
						// element styles within the block type styles take
						// precedence over these.
						Object.entries(
							typedVariation?.elements ?? {}
						).forEach( ( [ element, elementStyles ] ) => {
							if (
								elementStyles &&
								ELEMENTS[ element as ElementName ]
							) {
								variationNodesToAdd.push( {
									styles: elementStyles,
									selector: scopeSelector(
										variationSelector,
										ELEMENTS[ element as ElementName ]
									),
									elementName: element,
									isStyleVariation: true,
								} );
							}
						} );

						// Process the variations inner block type styles.
						Object.entries( typedVariation?.blocks ?? {} ).forEach(
							( [
								variationBlockName,
								variationBlockStyles,
							] ) => {
								const variationBlockSelector =
									typeof blockSelectors !== 'string'
										? scopeSelector(
												variationSelector,
												blockSelectors[
													variationBlockName
												]?.selector
										  )
										: undefined;
								const variationDuotoneSelector =
									typeof blockSelectors !== 'string'
										? scopeSelector(
												variationSelector,
												blockSelectors[
													variationBlockName
												]?.duotoneSelector as string
										  )
										: undefined;
								const variationFeatureSelectors =
									typeof blockSelectors !== 'string'
										? scopeFeatureSelectors(
												variationSelector,
												blockSelectors[
													variationBlockName
												]?.featureSelectors ?? {}
										  )
										: undefined;

								const variationBlockStyleNodes =
									pickStyleAndPseudoKeys(
										variationBlockStyles,
										variationBlockName
									);

								if ( variationBlockStyles?.css ) {
									variationBlockStyleNodes.css =
										variationBlockStyles.css;
								}

								if (
									! variationBlockSelector ||
									typeof blockSelectors === 'string'
								) {
									return;
								}

								variationNodesToAdd.push( {
									selector: variationBlockSelector,
									name: variationBlockName,
									isStyleVariation: true,
									duotoneSelector: variationDuotoneSelector,
									featureSelectors: variationFeatureSelectors,
									fallbackGapValue:
										blockSelectors[ variationBlockName ]
											?.fallbackGapValue,
									hasLayoutSupport:
										blockSelectors[ variationBlockName ]
											?.hasLayoutSupport,
									styles: variationBlockStyleNodes,
								} );

								// Process element styles for the inner blocks
								// of the variation.
								Object.entries(
									variationBlockStyles.elements ?? {}
								).forEach(
									( [
										variationBlockElement,
										variationBlockElementStyles,
									] ) => {
										if (
											variationBlockElementStyles &&
											ELEMENTS[
												variationBlockElement as ElementName
											]
										) {
											variationNodesToAdd.push( {
												styles: variationBlockElementStyles,
												selector: scopeSelector(
													variationBlockSelector,
													ELEMENTS[
														variationBlockElement as ElementName
													]
												),
												elementName:
													variationBlockElement,
												isStyleVariation: true,
											} );
										}
									}
								);
							}
						);
					}
				);
			}

			if (
				typeof blockSelectors !== 'string' &&
				blockSelectors?.[ blockName ]?.selector
			) {
				nodes.push( {
					duotoneSelector:
						blockSelectors[ blockName ].duotoneSelector,
					fallbackGapValue:
						blockSelectors[ blockName ].fallbackGapValue,
					hasLayoutSupport:
						blockSelectors[ blockName ].hasLayoutSupport,
					selector: blockSelectors[ blockName ].selector,
					styles: blockStyles,
					featureSelectors:
						blockSelectors[ blockName ].featureSelectors,
					name: blockName,
				} );
			}

			nodes.push( ...variationStyleNodesToAdd );

			Object.entries( typedNode?.elements ?? {} ).forEach(
				( [ elementName, value ] ) => {
					if (
						typeof blockSelectors !== 'string' &&
						value &&
						blockSelectors?.[ blockName ] &&
						ELEMENTS[ elementName as ElementName ]
					) {
						nodes.push( {
							styles: value,
							selector: blockSelectors[ blockName ]?.selector
								.split( ',' )
								.map( ( sel: string ) => {
									const elementSelectors =
										ELEMENTS[
											elementName as ElementName
										].split( ',' );
									return elementSelectors.map(
										( elementSelector: string ) =>
											sel + ' ' + elementSelector
									);
								} )
								.join( ',' ),
							elementName,
						} );
					}
				}
			);

			// Add variation nodes AFTER the main block and its elements
			// to match PHP processing order.
			nodes.push( ...variationNodesToAdd );
		}
	);

	return nodes;
};

export const getNodesWithSettings = (
	tree: GlobalStylesConfig,
	blockSelectors: string | BlockSelectors
): any[] => {
	const nodes: {
		presets: Record< string, any >;
		custom?: Record< string, any >;
		selector?: string;
		duotoneSelector?: string;
		fallbackGapValue?: string;
		hasLayoutSupport?: boolean;
		featureSelectors?:
			| string
			| Record< string, string | Record< string, string > >;
		styleVariationSelectors?: Record< string, string >;
	}[] = [];

	if ( ! tree?.settings ) {
		return nodes;
	}

	const pickPresets = ( treeToPickFrom: any ): any => {
		let presets = {};
		PRESET_METADATA.forEach( ( { path } ) => {
			const value = getValueFromObjectPath( treeToPickFrom, path, false );
			if ( value !== false ) {
				presets = setImmutably( presets, path, value );
			}
		} );
		return presets;
	};

	// Top-level.
	const presets = pickPresets( tree.settings );
	const custom = tree.settings?.custom;
	if ( Object.keys( presets ).length > 0 || custom ) {
		nodes.push( {
			presets,
			custom,
			selector: ROOT_CSS_PROPERTIES_SELECTOR,
		} );
	}

	// Blocks.
	Object.entries( tree.settings?.blocks ?? {} ).forEach(
		( [ blockName, node ] ) => {
			const blockCustom = node.custom;
			if (
				typeof blockSelectors === 'string' ||
				! blockSelectors[ blockName ]
			) {
				return;
			}
			const blockPresets = pickPresets( node );
			if ( Object.keys( blockPresets ).length > 0 || blockCustom ) {
				nodes.push( {
					presets: blockPresets,
					custom: blockCustom,
					selector: blockSelectors[ blockName ]?.selector,
					featureSelectors:
						blockSelectors[ blockName ]?.featureSelectors,
				} );
			}
		}
	);

	return nodes;
};

/**
 * Resolves the selector for a given block support feature.
 *
 * If the block defines a feature-level selector (as a string or an object
 * with a `root` key), that selector is returned. Otherwise the fallback
 * selector is used.
 *
 * @param {string|Record<string,string|Record<string,string>>|undefined} featureSelectors The block's feature selectors.
 * @param {string}                                                       featureKey       The feature key to resolve.
 * @param {string}                                                       fallback         The default selector.
 * @return {string} The resolved selector.
 */
function resolveFeatureSelector(
	featureSelectors:
		| string
		| Record< string, string | Record< string, string > >
		| undefined,
	featureKey: string,
	fallback: string
): string {
	if ( ! featureSelectors || typeof featureSelectors === 'string' ) {
		return fallback;
	}

	const feature = featureSelectors[ featureKey ];
	if ( typeof feature === 'string' ) {
		return feature;
	}
	if ( typeof feature === 'object' && feature.root ) {
		return feature.root;
	}
	return fallback;
}

/**
 * Collects CSS variable declarations for a single preset metadata entry
 * across all origins.
 *
 * @param {Record<string,any>}             presets        The preset values keyed by origin.
 * @param {GlobalStylesConfig['settings']} mergedSettings The merged global styles settings.
 * @param {PresetMetadata}                 presetMetadata The preset metadata.
 * @return {string[]} The CSS variable declarations.
 */
function getPresetVarDeclarations(
	presets: Record< string, any >,
	mergedSettings: GlobalStylesConfig[ 'settings' ],
	{ path, valueKey, valueFunc, cssVarInfix }: PresetMetadata
): string[] {
	const presetByOrigin = getValueFromObjectPath(
		presets,
		path,
		[]
	) as PresetsByOrigin;

	const declarations: string[] = [];
	for ( const origin of [ 'default', 'theme', 'custom' ] ) {
		if ( ! presetByOrigin[ origin ] ) {
			continue;
		}
		for ( const value of presetByOrigin[ origin ] ) {
			const slug = kebabCase( value.slug );
			if ( valueKey && ! valueFunc ) {
				declarations.push(
					`--wp--preset--${ cssVarInfix }--${ slug }: ${ value[ valueKey ] }`
				);
			} else if ( valueFunc && typeof valueFunc === 'function' ) {
				declarations.push(
					`--wp--preset--${ cssVarInfix }--${ slug }: ${ valueFunc(
						value,
						mergedSettings
					) }`
				);
			}
		}
	}
	return declarations;
}

export const generateCustomProperties = (
	tree: GlobalStylesConfig,
	blockSelectors: BlockSelectors
): string => {
	const nodes = getNodesWithSettings( tree, blockSelectors );
	let ruleset = '';

	for ( const { presets, custom, selector, featureSelectors } of nodes ) {
		const defaultSelector = selector as string;

		/*
		 * Group preset declarations by selector. Blocks that define
		 * feature-level selectors need their preset CSS variables output
		 * under that feature selector instead of the block's root selector.
		 */
		const varsBySelector: Record< string, string[] > = {
			[ defaultSelector ]: [],
		};

		if ( tree?.settings ) {
			for ( const metadata of PRESET_METADATA ) {
				const declarations = getPresetVarDeclarations(
					presets,
					tree.settings,
					metadata
				);
				if ( declarations.length === 0 ) {
					continue;
				}

				const target = resolveFeatureSelector(
					featureSelectors,
					metadata.path[ 0 ],
					defaultSelector
				);
				if ( ! varsBySelector[ target ] ) {
					varsBySelector[ target ] = [];
				}
				varsBySelector[ target ].push( ...declarations );
			}
		}

		// Custom properties always use the block's default selector.
		const customProps = flattenTree( custom, '--wp--custom--', '--' );
		if ( customProps.length > 0 ) {
			varsBySelector[ defaultSelector ].push( ...customProps );
		}

		for ( const [ ruleSelector, declarations ] of Object.entries(
			varsBySelector
		) ) {
			if ( declarations.length > 0 ) {
				ruleset += `${ ruleSelector }{${ declarations.join( ';' ) };}`;
			}
		}
	}

	return ruleset;
};

/**
 * Renders CSS rules for a single style node.
 *
 * The node renderer handles feature-level selectors, duotone declarations,
 * layout styles, base declarations, and custom CSS. State nodes are expanded
 * before rendering so ordering matches the PHP renderer.
 *
 * @param node                          Style node metadata and styles.
 * @param context                       Render context and feature flags.
 * @param context.tree                  Global styles tree.
 * @param context.useRootPaddingAlign   Whether root padding alignment is enabled.
 * @param context.disableLayoutStyles   Whether layout styles are disabled.
 * @param context.hasBlockGapSupport    Whether block gap support is enabled.
 * @param context.hasFallbackGapSupport Whether fallback gap support is enabled.
 * @param context.disableRootPadding    Whether root padding declarations are disabled.
 * @return Rendered CSS rules for the node.
 */
function renderStylesNode(
	node: StylesNode,
	{
		tree,
		useRootPaddingAlign,
		disableLayoutStyles,
		hasBlockGapSupport,
		hasFallbackGapSupport,
		disableRootPadding,
	}: {
		tree: GlobalStylesConfig;
		useRootPaddingAlign?: boolean;
		disableLayoutStyles: boolean;
		hasBlockGapSupport?: boolean;
		hasFallbackGapSupport?: boolean;
		disableRootPadding: boolean;
	}
): string {
	const {
		selector,
		selectorSuffix,
		mediaQuery,
		duotoneSelector,
		styles,
		fallbackGapValue,
		hasLayoutSupport,
		featureSelectors,
		layoutSelector,
		layoutHasBlockGapSupport,
		skipSelectorWrapper,
		name,
	} = node;
	let ruleset = '';
	const effectiveSelector = selectorSuffix
		? appendToSelector( selector, selectorSuffix )
		: selector;

	// Process styles for block support features with custom feature level
	// CSS selectors set.
	if ( featureSelectors && typeof featureSelectors !== 'string' ) {
		let featureDeclarations = getFeatureDeclarations(
			featureSelectors,
			styles
		);

		// Update text indent selector for paragraph blocks based on the textIndent setting.
		featureDeclarations = updateParagraphTextIndentSelector(
			featureDeclarations,
			tree.settings,
			name
		);

		// Update button width declarations for percentage values to use calc() with block gap.
		featureDeclarations = updateButtonWidthDeclarations(
			featureDeclarations,
			tree.settings
		);

		Object.entries( featureDeclarations ).forEach(
			( [ featureSelector, declarations ] ) => {
				if ( declarations.length ) {
					const selectorForRule = selectorSuffix
						? appendToSelector( featureSelector, selectorSuffix )
						: featureSelector;
					const rules = declarations.join( ';' );
					ruleset += `:root :where(${ selectorForRule }){${ rules };}`;
				}
			}
		);
	}

	// Process duotone styles.
	if ( duotoneSelector ) {
		const duotoneStyles: any = {};
		if ( styles?.filter ) {
			duotoneStyles.filter = styles.filter;
			delete styles.filter;
		}
		const duotoneDeclarations = getStylesDeclarations( duotoneStyles );
		if ( duotoneDeclarations.length ) {
			ruleset += `${ duotoneSelector }{${ duotoneDeclarations.join(
				';'
			) };}`;
		}
	}

	// Process blockGap and layout styles.
	const selectorForLayout = layoutSelector ?? effectiveSelector;
	const hasBlockGapSupportForLayout =
		layoutHasBlockGapSupport ?? hasBlockGapSupport;
	if (
		! disableLayoutStyles &&
		( ROOT_BLOCK_SELECTOR === selectorForLayout || hasLayoutSupport )
	) {
		ruleset += getLayoutStyles( {
			style: styles,
			selector: selectorForLayout,
			hasBlockGapSupport: hasBlockGapSupportForLayout,
			hasFallbackGapSupport,
			fallbackGapValue,
		} );
	}

	// Process the remaining block styles (they use either normal block class or __experimentalSelector).
	const styleDeclarations = getStylesDeclarations(
		styles,
		effectiveSelector,
		useRootPaddingAlign,
		tree,
		disableRootPadding
	);
	if ( styleDeclarations?.length ) {
		const generalSelector = skipSelectorWrapper
			? effectiveSelector
			: `:root :where(${ effectiveSelector })`;
		ruleset += `${ generalSelector }{${ styleDeclarations.join( ';' ) };}`;
	}
	if ( styles?.css ) {
		ruleset += processCSSNesting(
			styles.css,
			`:root :where(${ effectiveSelector })`
		);
	}

	if ( mediaQuery && ruleset ) {
		return `${ mediaQuery }{${ ruleset }}`;
	}

	return ruleset;
}

export const transformToStyles = (
	tree: GlobalStylesConfig,
	blockSelectors: string | BlockSelectors,
	hasBlockGapSupport?: boolean,
	hasFallbackGapSupport?: boolean,
	disableLayoutStyles: boolean = false,
	disableRootPadding: boolean = false,
	styleOptions: Record< string, boolean > = {}
): string => {
	// These allow opting out of certain sets of styles.
	const options = {
		blockGap: true,
		blockStyles: true,
		layoutStyles: true,
		marginReset: true,
		presets: true,
		rootPadding: true,
		variationStyles: false,
		...styleOptions,
	};
	const nodesWithStyles = getNodesWithStyles( tree, blockSelectors );
	const nodesWithSettings = getNodesWithSettings( tree, blockSelectors );
	const useRootPaddingAlign = tree?.settings?.useRootPaddingAwareAlignments;
	const { contentSize, wideSize } = tree?.settings?.layout || {};
	const hasBodyStyles =
		options.marginReset || options.rootPadding || options.layoutStyles;

	let ruleset = '';

	if ( options.presets && ( contentSize || wideSize ) ) {
		ruleset += `${ ROOT_CSS_PROPERTIES_SELECTOR } {`;
		ruleset = contentSize
			? ruleset + ` --wp--style--global--content-size: ${ contentSize };`
			: ruleset;
		ruleset = wideSize
			? ruleset + ` --wp--style--global--wide-size: ${ wideSize };`
			: ruleset;
		ruleset += '}';
	}

	if ( hasBodyStyles ) {
		/*
		 * Reset default browser margin on the body element.
		 * This is set on the body selector **before** generating the ruleset
		 * from the `theme.json`. This is to ensure that if the `theme.json` declares
		 * `margin` in its `spacing` declaration for the `body` element then these
		 * user-generated values take precedence in the CSS cascade.
		 * @link https://github.com/WordPress/gutenberg/issues/36147.
		 */
		ruleset += ':where(body) {margin: 0;';

		// Root padding styles should be output for full templates, patterns and template parts.
		if ( options.rootPadding && useRootPaddingAlign ) {
			/*
			 * These rules reproduce the ones from https://github.com/WordPress/gutenberg/blob/79103f124925d1f457f627e154f52a56228ed5ad/lib/class-wp-theme-json-gutenberg.php#L2508
			 * almost exactly, but for the selectors that target block wrappers in the front end. This code only runs in the editor, so it doesn't need those selectors.
			 */
			ruleset += `padding-right: 0; padding-left: 0; padding-top: var(--wp--style--root--padding-top); padding-bottom: var(--wp--style--root--padding-bottom) }
				.has-global-padding { padding-right: var(--wp--style--root--padding-right); padding-left: var(--wp--style--root--padding-left); }
				.has-global-padding > .alignfull { margin-right: calc(var(--wp--style--root--padding-right) * -1); margin-left: calc(var(--wp--style--root--padding-left) * -1); }
				.has-global-padding :where(:not(.alignfull.is-layout-flow) > .has-global-padding:not(.wp-block-block, .alignfull)) { padding-right: 0; padding-left: 0; }
				.has-global-padding :where(:not(.alignfull.is-layout-flow) > .has-global-padding:not(.wp-block-block, .alignfull)) > .alignfull { margin-left: 0; margin-right: 0;
				`;
		}

		ruleset += '}';
	}

	if ( options.blockStyles ) {
		nodesWithStyles.forEach( ( node ) => {
			if ( node.isStyleVariation && ! options.variationStyles ) {
				return;
			}

			const responsiveNodes = getResponsiveStyleNodes( node );
			// Match PHP node order: base, responsive base, pseudo, responsive pseudo.
			[
				node,
				...responsiveNodes,
				...getPseudoStyleNodes( node ),
				...responsiveNodes.flatMap( getPseudoStyleNodes ),
			].forEach( ( expandedNode ) => {
				ruleset += renderStylesNode( expandedNode, {
					tree,
					useRootPaddingAlign,
					disableLayoutStyles,
					hasBlockGapSupport,
					hasFallbackGapSupport,
					disableRootPadding,
				} );
			} );
		} );
	}

	if ( options.layoutStyles ) {
		/* Add alignment / layout styles */
		ruleset =
			ruleset +
			'.wp-site-blocks > .alignleft { float: left; margin-right: 2em; }';
		ruleset =
			ruleset +
			'.wp-site-blocks > .alignright { float: right; margin-left: 2em; }';
		ruleset =
			ruleset +
			'.wp-site-blocks > .aligncenter { justify-content: center; margin-left: auto; margin-right: auto; }';
	}

	if ( options.blockGap && hasBlockGapSupport ) {
		// Use fallback of `0.5em` just in case, however if there is blockGap support, there should nearly always be a real value.
		const gapValue =
			getGapCSSValue( tree?.styles?.spacing?.blockGap ) || '0.5em';
		ruleset =
			ruleset +
			`:root :where(.wp-site-blocks) > * { margin-block-start: ${ gapValue }; margin-block-end: 0; }`;
		ruleset =
			ruleset +
			':root :where(.wp-site-blocks) > :first-child { margin-block-start: 0; }';
		ruleset =
			ruleset +
			':root :where(.wp-site-blocks) > :last-child { margin-block-end: 0; }';
	}

	if ( options.presets ) {
		nodesWithSettings.forEach( ( { selector, presets } ) => {
			if (
				ROOT_BLOCK_SELECTOR === selector ||
				ROOT_CSS_PROPERTIES_SELECTOR === selector
			) {
				// Do not add extra specificity for top-level classes.
				selector = '';
			}

			const classes = getPresetsClasses( selector, presets );
			if ( classes.length > 0 ) {
				ruleset += classes;
			}
		} );
	}

	return ruleset;
};

export function generateSvgFilters(
	tree: GlobalStylesConfig,
	blockSelectors: BlockSelectors
): string[] {
	const nodesWithSettings = getNodesWithSettings( tree, blockSelectors );
	return nodesWithSettings.flatMap( ( { presets } ) => {
		return getPresetsSvgFilters( presets );
	} );
}

const getSelectorsConfig = ( blockType: BlockType, rootSelector: string ) => {
	if (
		blockType?.selectors &&
		Object.keys( blockType.selectors ).length > 0
	) {
		return blockType.selectors;
	}

	const config: Record< string, string > = {
		root: rootSelector,
	};
	Object.entries( BLOCK_SUPPORT_FEATURE_LEVEL_SELECTORS ).forEach(
		( [ featureKey, featureName ] ) => {
			const featureSelector = getBlockSelector( blockType, featureKey );

			if ( featureSelector ) {
				config[ featureName ] = featureSelector;
			}
		}
	);

	return config;
};

export const getBlockSelectors = (
	blockTypes: BlockType[],
	variationInstanceId?: string
) => {
	const { getBlockStyles } = select( blocksStore );
	const result: BlockSelectors = {};
	blockTypes.forEach( ( blockType ) => {
		const name = blockType.name;
		const selector = getBlockSelector( blockType );

		if ( ! selector ) {
			return; // Skip blocks without valid selectors
		}
		let duotoneSelector = getBlockSelector( blockType, 'filter.duotone' );
		// Keep backwards compatibility for support.color.__experimentalDuotone.
		if ( ! duotoneSelector ) {
			const rootSelector = getBlockSelector( blockType );
			const duotoneSupport = getBlockSupport(
				blockType,
				'color.__experimentalDuotone',
				false
			);
			if ( typeof duotoneSupport === 'string' && rootSelector ) {
				duotoneSelector = scopeSelector( rootSelector, duotoneSupport );
			}
		}

		const hasLayoutSupport =
			!! blockType?.supports?.layout ||
			!! blockType?.supports?.__experimentalLayout;
		const fallbackGapValue =
			// @ts-expect-error
			blockType?.supports?.spacing?.blockGap?.__experimentalDefault;

		const blockStyleVariations = getBlockStyles( name );
		const styleVariationSelectors: Record< string, string > = {};
		blockStyleVariations?.forEach( ( variation ) => {
			const variationSuffix = variationInstanceId
				? `-${ variationInstanceId }`
				: '';
			const variationName = `${ variation.name }${ variationSuffix }`;
			const styleVariationSelector = getBlockStyleVariationSelector(
				variationName,
				selector
			);

			styleVariationSelectors[ variationName ] = styleVariationSelector;
		} );

		// For each block support feature add any custom selectors.
		const featureSelectors = getSelectorsConfig( blockType, selector );

		result[ name ] = {
			duotoneSelector: duotoneSelector ?? undefined,
			fallbackGapValue,
			featureSelectors: Object.keys( featureSelectors ).length
				? featureSelectors
				: undefined,
			hasLayoutSupport,
			name,
			selector,
			styleVariationSelectors: blockStyleVariations?.length
				? styleVariationSelectors
				: undefined,
		};
	} );

	return result;
};

/**
 * If there is a separator block whose color is defined in theme.json via background,
 * update the separator color to the same value by using border color.
 *
 * @param config Theme.json configuration file object
 * @return Theme.json configuration file object updated
 */
function updateConfigWithSeparator(
	config: GlobalStylesConfig
): GlobalStylesConfig {
	const blocks = config.styles?.blocks;
	const separatorBlock = blocks?.[ 'core/separator' ];
	const needsSeparatorStyleUpdate =
		separatorBlock &&
		separatorBlock.color?.background &&
		! separatorBlock.color?.text &&
		! separatorBlock.border?.color;
	if ( needsSeparatorStyleUpdate ) {
		return {
			...config,
			styles: {
				...config.styles,
				blocks: {
					...blocks,
					'core/separator': {
						...separatorBlock,
						color: {
							...separatorBlock.color,
							text: separatorBlock.color?.background,
						},
					},
				},
			},
		};
	}
	return config;
}

export function processCSSNesting( css: string, blockSelector: string ) {
	let processedCSS = '';

	if ( ! css || css.trim() === '' ) {
		return processedCSS;
	}

	// Split CSS nested rules.
	const parts = css.split( '&' );
	parts.forEach( ( part: string ) => {
		if ( ! part || part.trim() === '' ) {
			return;
		}

		const isRootCss = ! part.includes( '{' );
		if ( isRootCss ) {
			// If the part doesn't contain braces, it applies to the root level.
			processedCSS += `:root :where(${ blockSelector }){${ part.trim() }}`;
		} else {
			// If the part contains braces, it's a nested CSS rule.
			const splitPart = part.replace( '}', '' ).split( '{' );
			if ( splitPart.length !== 2 ) {
				return;
			}

			const [ nestedSelector, cssValue ] = splitPart;

			// Handle pseudo elements such as ::before, ::after, etc. Regex will also
			// capture any leading combinator such as >, +, or ~, as well as spaces.
			// This allows pseudo elements as descendants e.g. `.parent ::before`.
			const matches = nestedSelector.match( /([>+~\s]*::[a-zA-Z-]+)/ );
			const pseudoPart = matches ? matches[ 1 ] : '';
			const withoutPseudoElement = matches
				? nestedSelector.replace( pseudoPart, '' ).trim()
				: nestedSelector.trim();

			let combinedSelector;
			if ( withoutPseudoElement === '' ) {
				// Only contained a pseudo element to use the block selector to form
				// the final `:root :where()` selector.
				combinedSelector = blockSelector;
			} else {
				// If the nested selector is a descendant of the block scope it with the
				// block selector. Otherwise append it to the block selector.
				combinedSelector = nestedSelector.startsWith( ' ' )
					? scopeSelector( blockSelector, withoutPseudoElement )
					: appendToSelector( blockSelector, withoutPseudoElement );
			}

			// Build final rule, re-adding any pseudo element outside the `:where()`
			// to maintain valid CSS selector.
			processedCSS += `:root :where(${ combinedSelector })${ pseudoPart }{${ cssValue.trim() }}`;
		}
	} );
	return processedCSS;
}

export interface GlobalStylesRenderOptions {
	hasBlockGapSupport?: boolean;
	hasFallbackGapSupport?: boolean;
	disableLayoutStyles?: boolean;
	disableRootPadding?: boolean;
	getBlockStyles?: ( blockName: string ) => any[];
	styleOptions?: Record< string, boolean >;
}

/**
 * Returns the global styles output based on the current state of global styles config loaded in the editor context.
 *
 * @param config     Global styles configuration
 * @param blockTypes Array of block types from WordPress blocks store
 * @param options    Options for rendering global styles
 * @return Array of stylesheets and settings
 */
export function generateGlobalStyles(
	config: GlobalStylesConfig | undefined = {},
	blockTypes: any[] = [],
	options: GlobalStylesRenderOptions = {}
): [ any[], any ] {
	const {
		hasBlockGapSupport: hasBlockGapSupportOption,
		hasFallbackGapSupport: hasFallbackGapSupportOption,
		disableLayoutStyles = false,
		disableRootPadding = false,
		styleOptions = {},
	} = options;

	// Use provided block types or fall back to getBlockTypes()
	const blocks = blockTypes.length > 0 ? blockTypes : getBlockTypes();

	const blockGap = getSetting( config, 'spacing.blockGap' );
	const hasBlockGapSupport = hasBlockGapSupportOption ?? blockGap !== null;
	const hasFallbackGapSupport =
		hasFallbackGapSupportOption ?? ! hasBlockGapSupport;

	if ( ! config?.styles || ! config?.settings ) {
		return [ [], {} ];
	}
	const updatedConfig = updateConfigWithSeparator( config );
	const blockSelectors = getBlockSelectors( blocks );
	const customProperties = generateCustomProperties(
		updatedConfig,
		blockSelectors
	);
	const globalStyles = transformToStyles(
		updatedConfig,
		blockSelectors,
		hasBlockGapSupport,
		hasFallbackGapSupport,
		disableLayoutStyles,
		disableRootPadding,
		styleOptions
	);
	const svgs = generateSvgFilters( updatedConfig, blockSelectors );
	const styles = [
		{
			css: customProperties,
			isGlobalStyles: true,
		},
		{
			css: globalStyles,
			isGlobalStyles: true,
		},
		// Load custom CSS in own stylesheet so that any invalid CSS entered in the input won't break all the global styles in the editor.
		{
			css: updatedConfig?.styles?.css ?? '',
			isGlobalStyles: true,
		},
		{
			assets: svgs,
			__unstableType: 'svgs',
			isGlobalStyles: true,
		},
	];

	// Loop through the blocks to check if there are custom CSS values.
	// If there are, get the block selector and push the selector together with
	// the CSS value to the 'stylesheets' array.
	blocks.forEach( ( blockType: BlockType ) => {
		const blockStyles = updatedConfig?.styles?.blocks?.[ blockType.name ];
		if ( blockStyles?.css ) {
			const { featureSelectors } = blockSelectors[ blockType.name ];
			const cssFeatureSelector =
				typeof featureSelectors === 'object'
					? featureSelectors?.css
					: undefined;
			let resolvedCssSelector: string | undefined;
			if ( typeof cssFeatureSelector === 'string' ) {
				resolvedCssSelector = cssFeatureSelector;
			} else if ( typeof cssFeatureSelector === 'object' ) {
				resolvedCssSelector = (
					cssFeatureSelector as Record< string, string >
				 )?.root;
			}
			const selector =
				resolvedCssSelector ??
				blockSelectors[ blockType.name ].selector;
			styles.push( {
				css: processCSSNesting( blockStyles.css, selector ),
				isGlobalStyles: true,
			} );
		}
	} );

	return [ styles, updatedConfig.settings ];
}
